import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Phone, Copy, Loader2 } from 'lucide-react';
import { apiPost } from '../../lib/api.js';
import { fmtPhone } from '../../lib/format.js';
import { lineTypeLabel, lineBadge, lineTone } from '../../lib/phoneLine.js';

/**
 * The lead's number with its line type and carrier: "(555) 012-3456 • Mobile (AT&T)".
 * When the line has not been looked up yet, "Check line type" runs a (billed) carrier lookup on demand.
 * @param {{lead: object, leadId: string, onChange?: () => void}} props
 */
export default function PhoneLineRow({ lead, leadId, onChange }) {
  const [busy, setBusy] = useState(false);
  const known = !!lineTypeLabel(lead?.phoneLineType);

  async function copyNumber() {
    try { await navigator.clipboard.writeText(lead.phone); toast.success('Phone copied'); } catch { toast.error('Copy failed'); }
  }

  async function check() {
    setBusy(true);
    try {
      const r = await apiPost(`/leads/${leadId}/lookup-phone`, {});
      if (r.simulated) toast('Line lookup needs Twilio to be connected.', { icon: 'ℹ️' });
      else { toast.success(r.lineType === 'unknown' ? 'Line type could not be determined' : `Line type: ${lineTypeLabel(r.lineType)}`); onChange?.(); }
    } catch (e) {
      toast.error(e?.response?.data?.error === 'lookup_failed' ? 'Line lookup failed — try again later' : (e?.response?.data?.error || 'Line lookup failed'));
    }
    setBusy(false);
  }

  return (
    <div className="space-y-1" data-testid="phone-line-row">
      <button type="button" onClick={copyNumber} className="group flex items-center gap-1.5 text-xs text-white/80 hover:text-white">
        <Phone className="h-3.5 w-3.5 text-white/40" />
        {fmtPhone(lead.phone)}
        <Copy className="h-3 w-3 opacity-0 group-hover:opacity-60" />
      </button>
      <div className="flex flex-wrap items-center gap-1.5 pl-5 text-[11px]">
        {known ? (
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-semibold ${lineTone(lead.phoneLineType)}`} data-testid="line-badge">
            {lineBadge(lead)}
          </span>
        ) : (
          <button type="button" onClick={check} disabled={busy} className="inline-flex items-center gap-1 text-civix-purpleSoft underline hover:text-white disabled:opacity-50" data-testid="check-line-type">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} Check line type
          </button>
        )}
      </div>
    </div>
  );
}
